import React, { useCallback, useMemo } from 'react';
import { FileText, Folder } from 'lucide-react';
import { InfiniteScroll } from '@/components/common/InfiniteScroll/InfiniteScroll';
import { TFileItem, TFileSubfoldersItem, RequestParams } from '@/data/types';
import FileGridCard from './FileGridCard';
import FolderGridCard from './FolderGridCard';
import FileListRow from './FileListRow';
import FolderItem from '@/components/ui/Folder';

interface FilesListProps {
  attemptGetAllFiles: (params: RequestParams) => Promise<any>;
  filesData: TFileItem[] | undefined;
  subfoldersData: TFileSubfoldersItem[] | undefined;
  viewMode: 'grid' | 'list';
  searchTerm: string;
  openDropdown: string | null;
  onDropdownToggle: (fileId: string) => void;
  // Helper functions passed down to avoid re-creating them
  getFileIcon: (file: TFileItem) => React.ReactNode;
  setAbacFile: (file: TFileItem | null) => void;
  formatFileSize: (bytes: number | null | undefined) => string;
  formatDate: (dateString: string) => string;
  getFileCategory: (file: TFileItem) => string;
  canDownloadFile: (file: TFileItem) => boolean;
  onPreview: (file: TFileItem) => void;
  onDownload: (file: TFileItem) => void;
  onMove: (file: TFileItem) => void;
  onDelete: (file: TFileItem) => void;
  // Navigation props
  currentPath: string;
  onNavigateToPath: (path: string) => void;
  // Folder creation
  onCreateFolder: (folderName: string) => void;
  // File upload
  onUploadSuccess: () => void;
  // Infinite scroll refresh
  infiniteScrollKey: number;
}

const FilesList: React.FC<FilesListProps> = React.memo(
  ({
    attemptGetAllFiles,
    filesData = [],
    subfoldersData = [],
    viewMode,
    searchTerm,
    openDropdown,
    onDropdownToggle,
    getFileIcon,
    formatFileSize,
    formatDate,
    getFileCategory,
    canDownloadFile,
    onPreview,
    onDownload,
    onMove,
    onDelete,
    currentPath,
    onNavigateToPath,
    onCreateFolder,
    onUploadSuccess,
    infiniteScrollKey,
    setAbacFile,
  }) => {
    // Extract folders for current path from subfolders data
    const currentFolders = useMemo(() => {
      if (!subfoldersData || subfoldersData.length === 0) return [];

      return subfoldersData
        .filter((subfolder) => {
          // Filter subfolders that are direct children of current path
          if (currentPath === '/') {
            // For root, include folders that don't have multiple slashes (direct children)
            const pathWithoutLeadingSlash = subfolder.directory.substring(1);
            return pathWithoutLeadingSlash && !pathWithoutLeadingSlash.includes('/');
          } else {
            // For nested paths, check if it starts with current path and is a direct child
            if (!subfolder.directory.startsWith(currentPath)) return false;

            const remainingPath = subfolder.directory.substring(currentPath.length);
            // Should start with / and not contain additional /
            return remainingPath.startsWith('/') && !remainingPath.substring(1).includes('/');
          }
        })
        .map((subfolder) => {
          // Extract folder name from full path
          const folderName =
            currentPath === '/'
              ? subfolder.directory.substring(1)
              : subfolder.directory.substring(currentPath.length + 1);

          return {
            name: folderName,
            fullPath: subfolder.directory,
            count: subfolder.count,
          };
        });
    }, [subfoldersData, currentPath]);

    // Handle folder click
    const handleFolderClick = useCallback(
      (folderPath: string) => {
        onNavigateToPath(folderPath);
      },
      [onNavigateToPath],
    );

    // Render function for files
    const renderFiles = useCallback(
      (files: TFileItem[], state: any) => {
        const hasContent = files.length > 0 || currentFolders.length > 0;

        if (!hasContent && !state.loading) {
          return (
            <div className="rounded-xl border border-gray-200 bg-white p-12 text-center shadow-sm">
              <div className="mx-auto h-16 w-16 rounded-full bg-gray-100 p-4">
                <FileText className="h-8 w-8 text-gray-400" />
              </div>
              <h3 className="mt-4 text-lg font-medium text-gray-900">Δεν βρέθηκαν αρχεία</h3>
              <p className="mt-2 text-gray-500">
                {searchTerm
                  ? 'Δοκιμάστε να προσαρμόσετε τους όρους αναζήτησής σας.'
                  : 'Ξεκινήστε μεταφορτώνοντας το πρώτο σας αρχείο χρησιμοποιώντας τα κουμπιά στην κορυφή.'}
              </p>
            </div>
          );
        }

        if (viewMode === 'grid') {
          return (
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
              {/* Folders first */}
              {currentFolders.map((folder, index) => (
                <div
                  key={folder.fullPath}
                  className="group cursor-pointer rounded-lg p-4 transition-all duration-200 hover:bg-gray-50"
                  onClick={() => handleFolderClick(folder.fullPath)}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.currentTarget.classList.add('bg-blue-50');
                  }}
                  onDragLeave={(e) => {
                    e.preventDefault();
                    e.currentTarget.classList.remove('bg-blue-50');
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.currentTarget.classList.remove('bg-blue-50');
                    const fileId = e.dataTransfer.getData('text/plain');
                    const file = filesData?.find((f) => f.id === fileId);
                    if (file) {
                      onMove({ ...file, path: folder.fullPath });
                    }
                  }}
                >
                  <div className="flex flex-col items-center p-4 text-center">
                    <FolderItem label={`${folder?.name} (${folder.count})`} />
                    {/* <img src="/assets/folder.png" alt="Folder" className="mb-3 h-32 w-32 object-contain" />
                    <div className="flex items-center justify-between gap-4">
                      <h3 className="flex w-full truncate text-sm font-medium text-gray-900">{folder.name}</h3>{' '}
                      <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800">
                        {folder.count}
                      </span>
                    </div> */}
                  </div>
                </div>
              ))}

              {/* Files */}
              {files.map((file, index) => (
                <FileGridCard
                  key={file.id}
                  file={file}
                  index={currentFolders.length + index}
                  openDropdown={openDropdown}
                  onDropdownToggle={onDropdownToggle}
                  getFileIcon={getFileIcon}
                  formatFileSize={formatFileSize}
                  formatDate={formatDate}
                  getFileCategory={getFileCategory}
                  canDownloadFile={canDownloadFile}
                  onPreview={onPreview}
                  onDownload={onDownload}
                  onMove={onMove}
                  onDelete={onDelete}
                  setAbacFile={setAbacFile}
                />
              ))}
            </div>
          );
        } else {
          return (
            <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
              <div className="grid grid-cols-12 gap-4 border-b border-gray-200 px-6 py-4 text-sm font-medium text-gray-500">
                <div className="col-span-5">Όνομα</div>
                <div className="col-span-2">Μέγεθος</div>
                <div className="col-span-2">Ημερομηνία</div>
                <div className="col-span-2">Κατηγορία</div>
                <div className="col-span-1"></div>
              </div>

              {/* Folders first */}
              {currentFolders.map((folder) => (
                <button
                  key={folder.fullPath}
                  onClick={() => handleFolderClick(folder.fullPath)}
                  className="grid w-full grid-cols-12 gap-4 px-6 py-4 text-left transition-colors hover:bg-gray-50"
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.currentTarget.classList.add('bg-blue-50');
                  }}
                  onDragLeave={(e) => {
                    e.preventDefault();
                    e.currentTarget.classList.remove('bg-blue-50');
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.currentTarget.classList.remove('bg-blue-50');
                    const fileId = e.dataTransfer.getData('text/plain');
                    const file = filesData?.find((f) => f.id === fileId);
                    if (file) {
                      onMove({ ...file, path: folder.fullPath });
                    }
                  }}
                >
                  <div className="col-span-5 flex items-center space-x-3">
                    <img src="/assets/folder.png" alt="Folder" className="h-10 w-10 object-contain" />
                    <span className="font-medium text-gray-900">{folder.name}</span>
                  </div>
                  <div className="col-span-2 flex items-center text-sm text-gray-500">—</div>
                  <div className="col-span-2 flex items-center text-sm text-gray-500">—</div>
                  <div className="col-span-2 flex items-center">
                    <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800">
                      {folder.count} files
                    </span>
                  </div>
                  <div className="col-span-1"></div>
                </button>
              ))}

              {/* Files */}
              {files.map((file, index) => (
                <FileListRow
                  key={file.id}
                  file={file}
                  index={index}
                  openDropdown={openDropdown}
                  onDropdownToggle={onDropdownToggle}
                  getFileIcon={getFileIcon}
                  formatFileSize={formatFileSize}
                  formatDate={formatDate}
                  getFileCategory={getFileCategory}
                  canDownloadFile={canDownloadFile}
                  onPreview={onPreview}
                  onDownload={onDownload}
                  onMove={onMove}
                  onDelete={onDelete}
                  setAbacFile={setAbacFile}
                />
              ))}
            </div>
          );
        }
      },
      [
        viewMode,
        searchTerm,
        openDropdown,
        onDropdownToggle,
        getFileIcon,
        formatFileSize,
        formatDate,
        getFileCategory,
        canDownloadFile,
        onPreview,
        onDownload,
        onMove,
        onDelete,
        currentPath,
        currentFolders,
        handleFolderClick,
        onCreateFolder,
      ],
    );

    // Loading component
    const renderLoading = useCallback((isInitial: boolean) => {
      if (isInitial) {
        return (
          <div className="flex items-center justify-center py-20">
            <div className="flex flex-col items-center">
              <div className="mb-4 h-12 w-12 animate-spin rounded-full border-4 border-blue-600 border-t-transparent"></div>
              <p className="text-gray-600">Φόρτωση αρχείων...</p>
            </div>
          </div>
        );
      }

      return (
        <div className="flex items-center justify-center py-8">
          <div className="mr-3 h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent"></div>
          <p className="text-gray-600">Φόρτωση περισσότερων...</p>
        </div>
      );
    }, []);

    // Error component
    const renderError = useCallback((error: string, retry: () => void) => {
      return (
        <div className="flex flex-col items-center justify-center py-20">
          <div className="mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-red-100">
            <svg className="h-8 w-8 text-red-500" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <h3 className="mb-2 text-lg font-medium text-gray-900">Σφάλμα φόρτωσης</h3>
          <p className="mb-4 max-w-md text-center text-gray-500">{error}</p>
          <button
            onClick={retry}
            className="rounded-lg bg-blue-600 px-4 py-2 text-white transition-colors hover:bg-blue-700"
          >
            Δοκιμάστε ξανά
          </button>
        </div>
      );
    }, []);

    return (
      <>
        <InfiniteScroll
          key={infiniteScrollKey}
          apiAction={attemptGetAllFiles}
          itemsData={filesData}
          initialPageSize={20}
          debug={false}
          renderLoading={renderLoading}
          renderError={renderError}
          params={{
            path: currentPath,
          }}
          search={{
            searchValue: searchTerm,
            debounceMs: 300,
            resetOnChange: true,
          }}
          scroll={{
            threshold: 300,
            debounceMs: 200,
          }}
          className="pb-8"
        >
          {renderFiles}
        </InfiniteScroll>
      </>
    );
  },
);

FilesList.displayName = 'FilesList';

export default FilesList;
